import { RenderRichText } from '@/components/helpers/cms/RenderRichText';
import { MlCopyBlock } from '@/components/molecules';
import { AtClickable } from '@/components/atoms/at-clickable/at-clickable';
import { AtDivider } from '@/components/atoms/at-divider/at-divider';
import { AtIcon } from '@/components/atoms/at-icon/at-icon';
import {
  EClickableSize,
  EClickableTarget,
  EClickableTypes,
  EClickableVariants,
  TClickableIconPosition,
} from '@/components/atoms/at-clickable/at-clickable.types';
import { EIcons } from '@/components/atoms/at-icon/icons';
import { EBlockAlignment, EThemes } from '@/lib/contentstack-sdk/types';
import {
  EDividerColors,
  EDividerDirectionalVariants,
  EDividerVariants,
} from '@/components/atoms/at-divider/at-divider.types';
import classNames from 'classnames';
import { IOrFloorPlanViewerProps } from './or-floorplan-viewer.types';
import orFloorplanViewer from './or-floorplan-viewer.variants';
import FloorPlanViewerClient from './or-floorplan-viewer.client';

export const OrFloorPlanViewer = ({
  heading,
  ctaProps,
  description,
  blueprints,
  eyebrow,
  subhead,
  theme = EThemes.LIGHT,
  floorPlansPDF,
  blockAlignment = 'LEFT',
  topSpacing = 'md',
  bottomSpacing = 'md',
  topDivider = false,
  bottomDivider = false,
}: IOrFloorPlanViewerProps) => {
  const hasBlueprints = Array.isArray(blueprints) && blueprints.length > 0;
  const classes = orFloorplanViewer({ topSpacing, bottomSpacing });

  return (
    <section
      className="mx-auto items-center bg-skin-primary"
      data-theme={theme}
    >
      <div className="container">
        {topDivider && (
          <AtDivider
            decorative
            color={EDividerColors.GREY}
            direction={EDividerDirectionalVariants.HORIZONTAL}
            intent={EDividerVariants.THIN}
          />
        )}
        <div
          className={classNames(
            'flex flex-col-reverse md:grid md:grid-cols-12',
            classes,
          )}
        >
          <aside
            className="md:col-span-5 md:px-6"
            data-testid="or-floorplan-viewer"
          >
            <MlCopyBlock
              header={heading.header}
              title={heading.title}
              key={heading.uid}
              eyebrow={eyebrow}
              subhead={subhead}
              titleElement={heading.heading_element?.select || 'h2'}
              titleVariant={
                heading.heading_variant?.select ||
                heading.heading_element?.select ||
                'h2'
              }
              alignment={
                EBlockAlignment[blockAlignment as keyof typeof EBlockAlignment]
              }
            >
              <RenderRichText body={description} />
              {ctaProps && floorPlansPDF && (
                <AtClickable
                  download
                  variant={EClickableVariants[ctaProps.variant]}
                  size={EClickableSize[ctaProps.size]}
                  label={ctaProps.url.title}
                  iconPosition={
                    ctaProps?.icon?.[0]?.position
                      ? (ctaProps.icon[0]
                          .position as unknown as TClickableIconPosition)
                      : 'none'
                  }
                  outline={ctaProps?.outline}
                  type={ctaProps.type.toLocaleLowerCase() as EClickableTypes}
                  target={EClickableTarget.BLANK}
                  ariaLabel={ctaProps?.aria_label}
                  href={floorPlansPDF.url}
                  className="[&_svg]:ml-4"
                  interactLocation="or-floor-plan-viewer"
                >
                  {ctaProps?.icon?.[0]?.name?.icon && (
                    <AtIcon
                      icon={ctaProps.icon[0].name.icon as EIcons}
                      color="var(--color-ui-stroke-primary)"
                    />
                  )}
                </AtClickable>
              )}
            </MlCopyBlock>
          </aside>
          <aside className="md:col-span-5 md:col-start-7">
            {hasBlueprints && (
              <FloorPlanViewerClient blueprints={blueprints} theme={theme} />
            )}
          </aside>
        </div>
        {bottomDivider && (
          <AtDivider
            decorative
            color={EDividerColors.GREY}
            direction={EDividerDirectionalVariants.HORIZONTAL}
            intent={EDividerVariants.THIN}
          />
        )}
      </div>
    </section>
  );
};
